import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, Send, Clock, CheckCircle, XCircle, DollarSign,
  MapPin, Calendar, FileText, ArrowRight, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import api from '../../services/api';

const CustomRequestsTab = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    preferred_date: '',
    preferred_time: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    estimated_duration_hours: '',
    budget_range: ''
  });

  const statuses = {
    pending: { label: 'Pending Review', color: 'bg-[var(--warning)]/15 text-yellow-700', icon: Clock },
    reviewing: { label: 'Under Review', color: 'bg-[#D4AF37]/10 text-[#D4AF37]', icon: Clock },
    quoted: { label: 'Quote Ready', color: 'bg-[#F4C2C2]/15 text-purple-700', icon: DollarSign },
    accepted: { label: 'Accepted', color: 'bg-[var(--success)]/15 text-green-700', icon: CheckCircle },
    declined: { label: 'Declined', color: 'bg-[var(--error)]/15 text-red-700', icon: XCircle },
    converted: { label: 'Booked', color: 'bg-teal-100 text-teal-700', icon: CheckCircle },
    cancelled: { label: 'Cancelled', color: 'bg-[var(--background-subtle)] text-[var(--text-primary)]', icon: XCircle }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await api.get('/custom-requests');
      setRequests(res.data);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.description || !formData.address) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/custom-requests', {
        ...formData,
        estimated_duration_hours: formData.estimated_duration_hours ? parseFloat(formData.estimated_duration_hours) : null
      });
      toast.success('Request submitted! We\'ll review it and send you a quote.');
      setShowForm(false);
      setFormData({
        title: '', description: '', preferred_date: '', preferred_time: '',
        address: '', city: '', state: '', zip_code: '',
        estimated_duration_hours: '', budget_range: ''
      });
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptQuote = async (requestId) => {
    try {
      const res = await api.post(`/custom-requests/${requestId}/accept`);
      toast.success('Quote accepted! Redirecting to payment...');
      if (res.data.booking_id) {
        navigate(`/customer/pay/${res.data.booking_id}`);
      }
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept quote');
    }
  };

  const handleDeclineQuote = async (requestId) => {
    if (!window.confirm('Are you sure you want to decline this quote?')) return;
    try {
      await api.post(`/custom-requests/${requestId}/decline`);
      toast.info('Quote declined');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to decline quote');
    }
  };

  const getStatusBadge = (status) => {
    const s = statuses[status] || { label: status, color: 'bg-[var(--background-subtle)] text-[var(--text-primary)]', icon: Clock };
    const Icon = s.icon;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${s.color}`}>
        <Icon className="w-3 h-3" />
        {s.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-[var(--text-muted)]">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[var(--primary)]" />
            Custom Requests
          </h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">Request anything - we'll send you a custom quote!</p>
        </div>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} className="btn-primary" data-testid="new-request-btn">
            <Sparkles className="w-4 h-4 mr-2" /> New Request
          </Button>
        )}
      </div>

      {/* New Request Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="card p-6 mb-6 border-2 border-[var(--primary)]"
          >
            <h3 className="text-lg font-bold mb-4">Request Anything</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">What do you need? *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Help moving furniture, Pet sitting for a weekend"
                  required
                  data-testid="request-title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Describe in detail *</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Please describe what you need, any specific requirements, time constraints, etc."
                  rows={4}
                  required
                  data-testid="request-description"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Preferred Date</label>
                  <Input
                    type="date"
                    value={formData.preferred_date}
                    onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Preferred Time</label>
                  <Input
                    type="time"
                    value={formData.preferred_time}
                    onChange={(e) => setFormData({ ...formData, preferred_time: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Service Address *</label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Street address"
                  required
                  data-testid="request-address"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">City *</label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">State *</label>
                  <Input
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">ZIP Code *</label>
                  <Input
                    value={formData.zip_code}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Estimated Duration (hours)</label>
                  <Input
                    type="number"
                    value={formData.estimated_duration_hours}
                    onChange={(e) => setFormData({ ...formData, estimated_duration_hours: e.target.value })}
                    placeholder="e.g., 2"
                    min="0.5"
                    step="0.5"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Your Budget Range</label>
                  <Input
                    value={formData.budget_range}
                    onChange={(e) => setFormData({ ...formData, budget_range: e.target.value })}
                    placeholder="e.g., $50-100"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="btn-primary flex-1" data-testid="submit-request-btn">
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" /> Submit Request</>
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Requests List */}
      {requests.length === 0 && !showForm ? (
        <div className="card p-12 text-center">
          <Sparkles className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
          <h3 className="text-lg font-bold mb-2">No Custom Requests Yet</h3>
          <p className="text-[var(--text-muted)] mb-4">
            Need something special? Submit a custom request and we'll send you a personalized quote!
          </p>
          <Button onClick={() => setShowForm(true)} className="btn-primary">
            <Sparkles className="w-4 h-4 mr-2" /> Make a Request
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <motion.div
              key={request.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`card p-5 ${request.status === 'quoted' ? 'border-2 border-purple-300 bg-[#F4C2C2]/10/30' : ''}`}
              data-testid={`request-${request.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {getStatusBadge(request.status)}
                  </div>
                  <h3 className="font-bold text-lg">{request.title}</h3>
                  <p className="text-sm text-[var(--text-muted)] line-clamp-2 mt-1">{request.description}</p>
                  
                  <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-[var(--text-muted)]">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {request.city}, {request.state}
                    </span>
                    {request.preferred_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {request.preferred_date}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(request.created_at).toLocaleDateString()}
                    </span>
                  </div>

                  {/* Quote Details */}
                  {request.status === 'quoted' && request.quoted_price && (
                    <div className="mt-4 p-4 bg-[#F4C2C2]/15 rounded-xl">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-purple-700 font-medium">Quote Received!</p>
                          <p className="text-2xl font-bold text-purple-800">${request.quoted_price}</p>
                          {request.quote_notes && (
                            <p className="text-sm text-[#F4C2C2] mt-1">{request.quote_notes}</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeclineQuote(request.id)}
                            className="border-purple-300 text-purple-700"
                          >
                            Decline
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleAcceptQuote(request.id)}
                            className="bg-purple-600 hover:bg-purple-700 text-white"
                            data-testid={`accept-quote-${request.id}`}
                          >
                            Accept & Book <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {request.status === 'converted' && request.booking_id && (
                    <div className="mt-4 p-3 bg-[var(--success)]/15 rounded-lg">
                      <p className="text-sm text-green-700">
                        <CheckCircle className="w-4 h-4 inline mr-1" />
                        Converted to booking - check your bookings tab!
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomRequestsTab;
